'use client'
import { motion } from 'framer-motion'
import { useLang } from '@/context/LangContext'
import { t, toLocalDigits } from '@/lib/translations'
import { hostingFrameworks, hostingHub, hostingPages } from '@/data/hostingPages'
import { hostingPathForSlug } from '@/lib/hosting'
import { consoleLoginUrl } from '@/lib/urls'
import { s3AssetUrl } from '@/lib/s3'
import Icon from '@/components/Icon'

export default function HostingHubPage() {
  const lang = useLang()

  return (
    <>
      {/* [tag=hosting-hub-hero] */}
      <section className="relative overflow-hidden pt-28 pb-14 sm:pt-36 sm:pb-16">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_78%_-10%,rgb(var(--brand-rgb)/0.2),transparent_60%)]" />
          <div className="absolute inset-0 dot-pattern opacity-[0.35]" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#07070d] to-transparent" />
        </div>

        <div className="relative max-w-6xl mx-auto px-8">
          <nav
            aria-label="breadcrumb"
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#64748b]"
          >
            <a href={`/${lang}`} className="hover:text-white transition-colors">
              {t(lang, hostingHub.labels.home)}
            </a>
            <span aria-hidden="true">/</span>
            <span className="text-[#94a3b8]">{t(lang, hostingHub.labels.hub)}</span>
          </nav>

          <div className="mt-9 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-6"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1.5 text-xs text-[#94a3b8]">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand-400)]" aria-hidden="true" />
                {t(lang, hostingHub.hero.eyebrow)}
              </span>

              <h1
                className="mt-6 text-[2.05rem] sm:text-5xl lg:text-[3.3rem] font-bold tracking-tight text-white"
                style={{ lineHeight: 1.22 }}
              >
                <span className="block">{t(lang, hostingHub.hero.title)}</span>
                <span className="mt-1 block text-[var(--brand-300)]">
                  {t(lang, hostingHub.hero.titleAccent)}
                </span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-[#94a3b8] leading-relaxed max-w-xl">
                {t(lang, hostingHub.hero.lead)}
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <a
                  href={consoleLoginUrl}
                  className="text-center px-6 py-3 rounded-lg bg-gradient-to-r from-[var(--brand-600)] to-[var(--brand-500)] text-white text-sm font-medium shadow-[0_18px_40px_-20px_rgb(var(--brand-rgb)/0.9)] hover:from-[var(--brand-500)] hover:to-[var(--brand-300)] transition-all duration-200"
                >
                  {t(lang, hostingHub.hero.primaryCta)}
                </a>
                <a
                  href={`/${lang}/pricing/calculator`}
                  className="text-center px-6 py-3 rounded-lg border border-white/[0.1] text-[#94a3b8] text-sm font-medium hover:text-white hover:border-white/[0.25] transition-all duration-200"
                >
                  {t(lang, hostingHub.hero.secondaryCta)}
                </a>
              </div>

              <ul className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2">
                {hostingHub.hero.trust.map((item, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-[#64748b]">
                    <Icon
                      name="check"
                      className="w-3.5 h-3.5 shrink-0 text-[var(--brand-400)]"
                      strokeWidth={2.5}
                    />
                    {t(lang, item)}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="lg:col-span-6"
            >
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute -inset-6 rounded-[2rem] bg-[radial-gradient(circle_at_50%_45%,rgb(var(--brand-rgb)/0.2),transparent_65%)] blur-2xl"
                />
                <div className="relative rounded-2xl border border-white/[0.08] bg-[#0a0a13] p-6 shadow-[0_40px_120px_-50px_rgba(89,131,224,0.55)]">
                  <div className="grid grid-cols-4 gap-x-3 gap-y-6">
                    {hostingFrameworks.map((framework) => (
                      <div
                        key={framework.slug}
                        className="flex h-12 items-center justify-center rounded-lg border border-white/[0.05] bg-white/[0.03] p-2"
                      >
                        <img
                          src={s3AssetUrl(framework.slug === 'static' ? 'HTML5' : framework.slug)}
                          alt={framework.label}
                          className={`h-6 w-auto object-contain opacity-80 ${
                            framework.slug === 'nextjs' ? 'invert' : ''
                          }`}
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="mt-6 border-t border-white/[0.06] pt-4 text-center text-xs text-[#64748b]">
                    {t(lang, hostingHub.grid.stackCaption)}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* [tag=hosting-hub-grid] */}
      <section className="relative py-14 sm:py-16">
        <div className="relative max-w-6xl mx-auto px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            {t(lang, hostingHub.grid.heading)}
          </h2>
          <p className="mt-4 text-[#94a3b8] leading-relaxed max-w-2xl">
            {t(lang, hostingHub.grid.description)}
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {hostingFrameworks.map((framework, i) => {
              const live = Boolean(hostingPages[framework.slug])
              return (
                <motion.a
                  key={framework.slug}
                  href={`/${lang}/${hostingPathForSlug(framework.slug)}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.35, delay: i * 0.03 }}
                  className="group rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 hover:border-white/[0.14] hover:bg-white/[0.04] transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-base font-semibold text-white">{framework.label}</h3>
                    {!live && (
                      <span className="shrink-0 rounded-full border border-white/[0.08] px-2 py-0.5 text-[10px] text-[#64748b]">
                        {t(lang, hostingHub.grid.soon)}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-[#94a3b8] leading-relaxed">
                    {t(lang, framework.blurb)}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-[var(--brand-400)]">
                    {t(lang, hostingHub.labels.cardCta)}
                    <Icon
                      name="arrow-right"
                      className="w-3.5 h-3.5 rtl:rotate-180"
                      strokeWidth={1.5}
                    />
                  </span>
                </motion.a>
              )
            })}
          </div>
        </div>
      </section>

      {/* [tag=hosting-hub-how] */}
      <section className="relative py-16 sm:py-20">
        <div className="relative max-w-6xl mx-auto px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            {t(lang, hostingHub.how.heading)}
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {hostingHub.how.items.map((item, i) => (
              <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
                <span className="text-sm font-semibold text-[var(--brand-400)]">
                  {toLocalDigits(String(i + 1), lang)}
                </span>
                <h3 className="mt-3 text-base font-semibold text-white">{t(lang, item.title)}</h3>
                <p className="mt-2 text-sm text-[#94a3b8] leading-relaxed">{t(lang, item.body)}</p>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <a
              href={`/${lang}/paas`}
              className="inline-flex items-center gap-2 text-sm font-medium text-[#94a3b8] hover:text-white transition-colors"
            >
              {t(lang, {
                fa: 'جزئیات پلتفرم ابری',
                en: 'How the cloud platform works',
                ar: 'تفاصيل المنصة السحابية',
              })}
              <Icon name="arrow-right" className="w-4 h-4 rtl:rotate-180" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      {/* [tag=hosting-hub-cta] */}
      <section className="relative pb-24">
        <div className="relative max-w-4xl mx-auto px-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 text-center">
          <h2 className="text-2xl font-semibold text-white">{t(lang, hostingHub.cta.heading)}</h2>
          <p className="mt-3 text-[#94a3b8] leading-relaxed">{t(lang, hostingHub.cta.body)}</p>
          <a
            href={consoleLoginUrl}
            className="mt-7 inline-block px-6 py-3 rounded-lg bg-gradient-to-r from-[var(--brand-600)] to-[var(--brand-500)] text-white text-sm font-medium hover:from-[var(--brand-500)] hover:to-[var(--brand-300)] transition-all duration-200"
          >
            {t(lang, hostingHub.cta.button)}
          </a>
        </div>
      </section>
    </>
  )
}
